import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Globe, Hexagon } from 'lucide-react';
import styles from './GlassDemoPage.module.css';
import hexLeft from '../../shared/assets/illustrations/hex_left.png';
import hexRight from '../../shared/assets/illustrations/hex_right.png';

const DEFAULT_GLASS = {
	bg: 0.01,
	border: 0.065,
	shadowOuter: 0.018,
	shadowInset: 0.055,
	blur: 2.2,
};

function fmt(n) {
	const x = Number(n);
	if (Number.isNaN(x)) return '0';
	return String(Number(x.toFixed(4)));
}

export default function GlassDemoPage() {
	const [glass, setGlass] = useState(DEFAULT_GLASS);
	const [copied, setCopied] = useState(false);

	const glassStyle = useMemo(
		() => ({
			background: `rgba(255, 255, 255, ${glass.bg})`,
			border: `1px solid rgba(255, 255, 255, ${glass.border})`,
			boxShadow: `0 18px 52px rgba(15, 40, 30, ${glass.shadowOuter}), 0 1px 0 rgba(255, 255, 255, ${glass.shadowInset}) inset`,
			backdropFilter: `blur(${glass.blur}px)`,
			WebkitBackdropFilter: `blur(${glass.blur}px)`,
		}),
		[glass],
	);

	const cssSnippet = useMemo(
		() => `.glassCard {
  background: rgba(255, 255, 255, ${fmt(glass.bg)});
  border: 1px solid rgba(255, 255, 255, ${fmt(glass.border)});
  box-shadow: 0 18px 52px rgba(15, 40, 30, ${fmt(glass.shadowOuter)}),
    0 1px 0 rgba(255, 255, 255, ${fmt(glass.shadowInset)}) inset;
  backdrop-filter: blur(${fmt(glass.blur)}px);
  -webkit-backdrop-filter: blur(${fmt(glass.blur)}px);
}`,
		[glass],
	);

	const patch = key => e => {
		const v = parseFloat(e.target.value);
		setGlass(g => ({ ...g, [key]: v }));
	};

	const copyCss = useCallback(() => {
		navigator.clipboard.writeText(cssSnippet).then(
			() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			},
			() => {
				setCopied(false);
			},
		);
	}, [cssSnippet]);

	return (
		<div className={styles.page}>
			<div className={styles.bgGlow} aria-hidden />

			<header className={styles.header}>
				<Link to='/' className={styles.brand}>
					<Hexagon className={styles.brandIcon} size={36} strokeWidth={2} />
					<span className={styles.brandName}>FinPath</span>
				</Link>

				<nav className={styles.nav} aria-label='Demo navigation'>
					<Link to='/' className={styles.navLink}>
						Home
					</Link>
					<Link to='/about' className={styles.navLink}>
						About
					</Link>
					<span className={styles.navLink}>Services</span>
					<Link to='/contact' className={styles.navLink}>
						Contacts
					</Link>
				</nav>

				<div className={styles.headerActions}>
					<span className={styles.lang}>
						<Globe size={24} strokeWidth={1.5} aria-hidden />
						ENG
						<ChevronDown
							size={24}
							strokeWidth={1.5}
							aria-hidden
							className={styles.langChevron}
						/>
					</span>
					<Link to='/sign-in' className={styles.loginLink}>
						Log In
						<ArrowRight size={24} strokeWidth={2} aria-hidden />
					</Link>
				</div>
			</header>

			<main className={styles.main}>
				<div className={styles.hero}>
					<div className={styles.heroArt} aria-hidden>
						<img
							src={hexLeft}
							alt=''
							className={styles.hexLeft}
							draggable={false}
						/>
						<img
							src={hexRight}
							alt=''
							className={styles.hexRight}
							draggable={false}
						/>
					</div>
					<div className={styles.glassCard} style={glassStyle}>
						<h1 className={styles.headline}>
							Master your money. Reach your goals.
						</h1>
						<p className={styles.subhead}>
							Track, plan, and grow your finances — all in one smart app.
						</p>
						<Link to='/sign-up' className={styles.cta}>
							Get Started
							<ArrowRight size={20} aria-hidden />
						</Link>
					</div>
				</div>
			</main>

			<p className={styles.demoNote}>
				Visual demo only — open this screen via <strong>/demo/glass</strong>
			</p>

			<aside className={styles.cssFloat} aria-label='Generated CSS'>
				<div className={styles.cssFloatHeader}>
					<span className={styles.cssFloatTitle}>Glass CSS</span>
					<button type='button' className={styles.cssCopyBtn} onClick={copyCss}>
						{copied ? 'Copied' : 'Copy'}
					</button>
				</div>
				<pre className={styles.cssPre}>{cssSnippet}</pre>
			</aside>

			<div className={styles.tuner} role='region' aria-label='Glass tuning'>
				<div className={styles.tunerInner}>
					<div className={styles.tunerRow}>
						<label className={styles.tunerLabel} htmlFor='glass-bg'>
							Fill opacity{' '}
							<span className={styles.tunerVal}>{fmt(glass.bg)}</span>
						</label>
						<input
							id='glass-bg'
							className={styles.tunerRange}
							type='range'
							min={0}
							max={0.12}
							step={0.002}
							value={glass.bg}
							onChange={patch('bg')}
						/>
					</div>
					<div className={styles.tunerRow}>
						<label className={styles.tunerLabel} htmlFor='glass-border'>
							Border opacity{' '}
							<span className={styles.tunerVal}>{fmt(glass.border)}</span>
						</label>
						<input
							id='glass-border'
							className={styles.tunerRange}
							type='range'
							min={0}
							max={0.35}
							step={0.005}
							value={glass.border}
							onChange={patch('border')}
						/>
					</div>
					<div className={styles.tunerRow}>
						<label className={styles.tunerLabel} htmlFor='glass-blur'>
							Blur (px){' '}
							<span className={styles.tunerVal}>{fmt(glass.blur)}</span>
						</label>
						<input
							id='glass-blur'
							className={styles.tunerRange}
							type='range'
							min={0}
							max={28}
							step={0.1}
							value={glass.blur}
							onChange={patch('blur')}
						/>
					</div>
					<div className={styles.tunerRow}>
						<label className={styles.tunerLabel} htmlFor='glass-outer'>
							Drop shadow opacity{' '}
							<span className={styles.tunerVal}>{fmt(glass.shadowOuter)}</span>
						</label>
						<input
							id='glass-outer'
							className={styles.tunerRange}
							type='range'
							min={0}
							max={0.12}
							step={0.002}
							value={glass.shadowOuter}
							onChange={patch('shadowOuter')}
						/>
					</div>
					<div className={styles.tunerRow}>
						<label className={styles.tunerLabel} htmlFor='glass-inset'>
							Inset highlight opacity{' '}
							<span className={styles.tunerVal}>{fmt(glass.shadowInset)}</span>
						</label>
						<input
							id='glass-inset'
							className={styles.tunerRange}
							type='range'
							min={0}
							max={0.3}
							step={0.005}
							value={glass.shadowInset}
							onChange={patch('shadowInset')}
						/>
					</div>
					<button
						type='button'
						className={styles.tunerReset}
						onClick={() => setGlass(DEFAULT_GLASS)}
					>
						Reset to defaults
					</button>
				</div>
			</div>
		</div>
	);
}
